/**
 * Events Service für API-Calls
 * Lädt Events aus der Datenbank
 */

import { apiFetch } from '../config/api'

export interface Event {
  id: number
  slug: string
  title: string
  date: string // Format: "2026-01-31"
  time: string | null // Format: "20:30:00"
  description: string
  full_description: string
  price: string
  image_url: string // Rückwärtskompatibilität
  preview_image_url: string | null // Für /partys Übersicht (4:3 Format)
  flyer_image_url: string | null // Für Event-Detailseite
  location: string | null
  category: string | null
  status: 'Geplant' | 'Aktiv' | 'Abgeschlossen' | 'Abgesagt'
  featured?: boolean // Optional: Featured Event
  created_at: string
  updated_at: string
}

export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

/**
 * Formatiere Datum für Anzeige
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const months = ['JAN', 'FEB', 'MÄR', 'APR', 'MAI', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEZ']
  const day = date.getDate()
  const month = months[date.getMonth()]
  return `${day}. ${month}`
}

/**
 * Formatiere vollständiges Datum
 */
function formatFullDate(dateString: string): string {
  const date = new Date(dateString)
  const months = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ]
  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()
  return `${day}. ${month} ${year}`
}

/**
 * Formatiere Uhrzeit
 */
function formatTime(timeString: string | null): string {
  if (!timeString) return ''
  const [hours, minutes] = timeString.split(':')
  return `${hours}:${minutes} Uhr`
}

/**
 * GET: Alle aktiven Events abrufen
 * Gibt immer ein Array zurück (leer bei Fehler)
 */
export async function getEvents(): Promise<Event[]> {
  try {
    const response = await apiFetch('/events', {
      method: 'GET',
    })

    if (!response.ok) {
      console.error('Fehler beim Laden der Events:', response.status)
      return []
    }

    const data: ApiResponse<Event[]> = await response.json()

    if (data.success && data.data) {
      return data.data
    }

    return []
  } catch (error) {
    console.error('Fehler beim Abrufen der Events:', error)
    return [] // Graceful Degradation: Leeres Array statt Crash
  }
}

/**
 * GET by Slug: Einzelnes Event abrufen
 * Wirft Error bei Fehler
 */
export async function getEventBySlug(slug: string): Promise<Event | null> {
  try {
    const response = await apiFetch(`/events/slug/${slug}`, {
      method: 'GET',
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Fehler beim Laden des Events: ${response.status}`)
    }

    const data: ApiResponse<Event> = await response.json()

    if (data.success && data.data) {
      return data.data
    }

    return null
  } catch (error) {
    console.error(`Fehler beim Abrufen des Events ${slug}:`, error)
    throw error
  }
}

/**
 * GET by ID: Einzelnes Event abrufen
 */
export async function getEventById(id: number): Promise<Event | null> {
  try {
    const response = await apiFetch(`/events/${id}`, {
      method: 'GET',
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null
      }
      throw new Error(`Fehler beim Laden des Events: ${response.status}`)
    }

    const data: ApiResponse<Event> = await response.json()

    if (data.success && data.data) {
      return data.data
    }

    return null
  } catch (error) {
    console.error(`Fehler beim Abrufen des Events ${id}:`, error)
    throw error
  }
}

/**
 * Transformiere Event für Frontend-Anzeige
 * Konvertiert Datenbank-Format in Frontend-Format
 */
export function transformEventForDisplay(event: Event) {
  // WICHTIG: Jedes Event muss sein eigenes Bild haben!
  // Priorität für Übersicht (previewImage):
  // 1. preview_image_url (spezifisches Vorschaubild für Übersicht)
  // 2. image_url (falls preview_image_url nicht vorhanden)
  // 3. flyer_image_url (nur als letzter Fallback, da oft gleich für mehrere Events)
  // 4. Fallback
  
  // Priorität für Detailseite (flyerImage):
  // 1. flyer_image_url (spezifischer Flyer für Detailseite)
  // 2. preview_image_url (falls flyer_image_url nicht vorhanden)
  // 3. image_url
  // 4. Fallback
  
  let previewImage: string
  let flyerImage: string
  
  // Helper-Funktion: Stelle sicher, dass URL mit / beginnt
  const normalizeImageUrl = (url: string | null | undefined): string | null => {
    if (!url || url.trim() === '') return null
    const trimmed = url.trim()
    // Stelle sicher, dass URL mit / beginnt
    return trimmed.startsWith('/') ? trimmed : '/' + trimmed
  }
  
  // Für Übersicht: preview_image_url hat höchste Priorität, dann image_url
  // flyer_image_url wird vermieden, da es oft das gleiche Bild für mehrere Events ist
  const normalizedPreview = normalizeImageUrl(event.preview_image_url)
  const normalizedImage = normalizeImageUrl(event.image_url)
  const normalizedFlyer = normalizeImageUrl(event.flyer_image_url)
  
  if (normalizedPreview) {
    previewImage = normalizedPreview
  } else if (normalizedImage) {
    previewImage = normalizedImage
  } else if (normalizedFlyer) {
    // Nur als letzter Fallback verwenden
    previewImage = normalizedFlyer
  } else {
    // Fallback nur wenn wirklich kein Bild vorhanden ist
    previewImage = '/flyer/scheunennacht.jpg'
  }
  
  // Für Detailseite: flyer_image_url hat Priorität
  if (normalizedFlyer) {
    flyerImage = normalizedFlyer
  } else if (normalizedPreview) {
    flyerImage = normalizedPreview
  } else if (normalizedImage) {
    flyerImage = normalizedImage
  } else {
    flyerImage = '/flyer/scheunennacht.jpg'
  }
  
  // Debug-Logging für Fehlerdiagnose
  if (import.meta.env.DEV) {
    console.log(`Event "${event.title}" (ID: ${event.id}, Slug: ${event.slug}):`, {
      preview_image_url: event.preview_image_url || 'nicht gesetzt',
      flyer_image_url: event.flyer_image_url || 'nicht gesetzt',
      image_url: event.image_url || 'nicht gesetzt',
      verwendetes_preview: previewImage,
      verwendetes_flyer: flyerImage
    })
  }
  
  // Warnung wenn kein spezifisches Bild vorhanden ist
  if (!event.preview_image_url && !event.image_url) {
    console.warn(`⚠️ Event "${event.title}" (ID: ${event.id}) verwendet Fallback oder flyer_image_url für Preview!`, {
      preview_image_url: event.preview_image_url,
      image_url: event.image_url,
      flyer_image_url: event.flyer_image_url,
      verwendetes_preview: previewImage
    })
  }
  
  return {
    id: event.id.toString(),
    slug: event.slug,
    title: event.title,
    date: formatDate(event.date),
    fullDate: formatFullDate(event.date),
    time: formatTime(event.time),
    price: event.price || 'Preis auf Anfrage',
    description: event.description || '',
    fullDescription: event.full_description || event.description || '',
    imageUrl: previewImage, // Für /partys Übersicht - sollte preview_image_url oder image_url sein
    flyerImageUrl: flyerImage, // Für Event-Detailseite
    galleryImages: [flyerImage],
    location: event.location || undefined,
    category: event.category || undefined,
  }
}

/**
 * Export Helper-Funktionen für Formatierung
 */
export { formatDate, formatFullDate, formatTime }

